import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';

import { getPublicLink, enroll, type W2PublicLink } from '../api/w2';
import { SELECTION_ROUTE } from '../selection/routes';
import { httpStatus, usePublicTokenResource } from '../usePublicTokenResource';
import { useW2Mode } from '../store/w2-mode';
import { W2_TID } from '../constants/testids';
import { W2Shell } from '../components/W2Shell';
import { W2LinkError } from '../components/W2LinkError';
import { useW2Paths } from './paths';

/**
 * Orchestrates the W2 employee flow (ADR-0010 revised / ADR-0011). It reuses the
 * ENTIRE branded B2C customer-facing flow — the branded register/login screens and
 * the full profile + PHQ (`W2Onboarding` → `OnboardingForm`: Welcome → Dependents
 * → the complete health questionnaire) — and keeps only the three W2 divergences:
 *
 *   1. employer link via token (resolve → `POST /employee/enroll`);
 *   2. forked persistence — the FULL payload lands in `employees.phqPayload` via
 *      `POST /employee/phq` (never `applications`); handled by the W2-mode gates in
 *      the B2C questionnaire + `useW2Mode.completePhq`;
 *   3. terminates right after the questionnaire — no plan / estimate / AI / payment
 *      / contract (the step list is trimmed in W2 mode).
 *
 *   resolve token (GET /public/link/:token, via usePublicTokenResource)
 *     ├─ transport outage → retry screen (NOT "invalid link" — arch W3/F3)
 *     ├─ Disabled / Archived / invalid → branded error, no flow
 *     └─ Active → activate W2 mode, then render the child routes:
 *          register (default) / login / forgot / reset → mint JWT (untouched
 *            B2C /auth/*) → enroll gate (POST /employee/enroll, token) →
 *                 Waived              → done (returning-employee lockout)
 *                 Completed           → /select-plans (JWT stage resolver)
 *                 else                → phq → forked submit → done
 */

/** Enroll-gate lifecycle. 'enrolling'/'error' render IN PLACE of the Outlet
 *  (frozen C-1: "not routes — transient in-session states"). */
type EnrollPhase = 'idle' | 'enrolling' | 'error';

export interface W2FlowOutletContext {
  /** The `:employerToken` path param (the employer enrollment token). */
  employerToken: string;
  /** The resolved Active link (branding / employerName). */
  link: W2PublicLink;
  /**
   * The shared enroll gate (moved from W2FlowPage): POST /employee/enroll with
   * the token, then NAVIGATE — Waived → done (state.reason 'returning'),
   * Completed → top-level `/select-plans`, else → phq. Both auth children
   * pass this as `onAuthenticated`; W2DoneRoute re-runs it on a reason-less load
   * with a JWT.
   */
  runEnroll: () => Promise<void>;
}

export const W2FlowLayout: React.FC = () => {
  const { employerToken = '' } = useParams<'employerToken'>();
  const navigate = useNavigate();
  const paths = useW2Paths();

  const activate = useW2Mode((s) => s.activate);
  const deactivate = useW2Mode((s) => s.deactivate);
  // Reactive gate inputs (render-time reads MUST be subscriptions — w2-mode.ts
  // store-access convention).
  const w2Active = useW2Mode((s) => s.active);
  const w2Token = useW2Mode((s) => s.token);

  const link = usePublicTokenResource(getPublicLink, employerToken);

  // Primitive projections so the activation effect keys on STABLE values, never
  // on the hook's per-render result object (risk R3).
  const ready = link.phase === 'ready';
  const status = ready ? (link.data?.status ?? null) : null;
  const employerName = ready ? link.data?.employerName : undefined;
  const isActiveLink = status === 'Active';

  const [enrollPhase, setEnrollPhase] = useState<EnrollPhase>('idle');

  // Once-only latch (R3): the token this layout instance last activated for.
  // A ref, not state — re-running the effect must be a no-op, not a re-render.
  const activatedTokenRef = useRef<string | null>(null);

  // Entering the flow for an Active link: JWT-clear + activate BEFORE any child
  // renders (children are gated below until `w2Active && w2Token === employerToken`).
  useEffect(() => {
    if (!isActiveLink) return;
    if (activatedTokenRef.current === employerToken) return; // once per token
    activatedTokenRef.current = employerToken;
    // Documented side effect (finding #3): a logged-in B2C customer who opens a
    // W2 link is signed out here. Narrowing this away is unsafe — the reused B2C
    // auth endpoints reject any request that still carries a session token, so
    // the token MUST be cleared before we can show register/login. We only touch
    // localStorage when a token is actually present, to keep the clear deliberate.
    if (localStorage.getItem('token')) {
      localStorage.removeItem('token');
    }
    activate(employerToken, employerName);
  }, [isActiveLink, employerName, employerToken, activate]);

  // Leaving `/e/:employerToken/*` entirely → untouched B2C behavior (C-7.3).
  // Intra-flow child navigation never unmounts the layout, so never fires this.
  useEffect(() => () => deactivate(), [deactivate]);

  const runEnroll = useCallback(async () => {
    setEnrollPhase('enrolling');
    try {
      const { phqStatus } = await enroll(employerToken);
      setEnrollPhase('idle');
      if (phqStatus === 'Waived') {
        // Waived employees declined coverage and stay excluded from selection.
        navigate(paths.done, { state: { reason: 'returning' } });
      } else if (phqStatus === 'Completed') {
        // Absolute, top-level route: leave `/e/:token` while preserving the JWT.
        // SelectionFlowPage resolves the employee's true stage server-side.
        navigate(SELECTION_ROUTE, { replace: true });
      } else {
        navigate(paths.phq);
      }
    } catch (err) {
      // Enroll failed after auth — the JWT exists, so DON'T bounce to auth
      // (re-registering would hit "email already exists"); offer a retry that
      // re-runs enroll with the session we already have (finding #8). Log
      // status-only for observability (never the payload/token) — finding M2.
      console.error('[w2] enroll failed', { status: httpStatus(err) });
      setEnrollPhase('error');
    }
  }, [employerToken, navigate, paths]);

  const outletContext = useMemo<W2FlowOutletContext | null>(
    () => (link.data ? { employerToken, link: link.data, runEnroll } : null),
    [link.data, employerToken, runEnroll]
  );

  // ---- Render states (moved verbatim from W2FlowPage) ----------------------

  const loadingShell = (
    <W2Shell title="One moment…">
      <p className="text-[15px] text-[#5a5a70]">Loading your enrollment…</p>
    </W2Shell>
  );

  switch (link.phase) {
    case 'loading':
      return loadingShell;
    case 'invalid':
      return <W2LinkError status={null} />;
    case 'outage':
      // Transport/server outage — distinct from a bad link (arch W3/F3): the
      // person is holding a possibly-valid emailed link, so offer a retry
      // instead of the "this link is invalid" copy.
      return (
        <W2Shell
          title="We couldn’t load your enrollment"
          description="Something went wrong on our side. Please try again in a moment."
        >
          <div data-testid={W2_TID.loadError.root}>
            <button
              type="button"
              data-testid={W2_TID.loadError.retry}
              onClick={link.retry}
              className="rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-white hover:opacity-90"
            >
              Try again
            </button>
          </div>
        </W2Shell>
      );
  }

  if (!isActiveLink) {
    // Resolved, but the link isn't open for enrollment (Disabled / Archived).
    return <W2LinkError status={link.data?.status ?? null} />;
  }

  // W2-mode-ON-before-children (C-7.1): hold the shell until the activation
  // effect committed FOR THIS token. `w2Token === employerToken` also masks the
  // one 'ready'-with-stale-data render during an /e/A → /e/B param change.
  const activated = w2Active && w2Token === employerToken;
  if (!activated || enrollPhase === 'enrolling' || !outletContext) {
    if (enrollPhase === 'enrolling') {
      return (
        <W2Shell title="One moment…" showLogout>
          <p className="text-[15px] text-[#5a5a70]">Loading your enrollment…</p>
        </W2Shell>
      );
    }
    return loadingShell; // 'activating'
  }

  if (enrollPhase === 'error') {
    // Auth already succeeded (JWT exists); offer a dedicated enroll retry rather
    // than sending the employee back to register/login (finding #8).
    return (
      <W2Shell
        showLogout
        title="We couldn’t finish your enrollment"
        description="Your sign-in worked, but we hit a problem enrolling you. Please try again."
      >
        <button
          type="button"
          onClick={runEnroll}
          className="rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-white hover:opacity-90"
        >
          Retry enrollment
        </button>
      </W2Shell>
    );
  }

  // `display: contents` wrapper — a pure e2e/testing seam (its presence ⇔ the
  // gate above passed ⇔ W2 mode is active for this token), zero layout impact.
  return (
    <div className="contents" data-testid={W2_TID.flow.root}>
      <Outlet context={outletContext} />
    </div>
  );
};

export function useW2FlowContext(): W2FlowOutletContext {
  // Non-null by construction: the layout renders the Outlet only after the
  // C-7.1 gate passes (link ready + mode active), and passes a non-null context.
  return useOutletContext<W2FlowOutletContext>();
}
